import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ROLES } from "../lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil, KeyRound } from "lucide-react";

const empty = { email: "", password: "", name: "", role: "consulta", company_ids: [] };

export default function Users() {
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
      setList(u.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error("Erro ao listar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.id) {
        const body = { name: form.name, role: form.role, company_ids: form.company_ids, active: form.active };
        if (form.password) body.password = form.password;
        await api.patch(`/users/${form.id}`, body);
      } else {
        await api.post("/users", form);
      }
      toast.success("Usuário salvo");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir usuário ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Acesso</div>
          <h1 className="text-3xl font-black tracking-tighter">Usuários e Permissões</h1>
          <p className="text-sm text-slate-600 mt-1">Controle de acesso por perfil e empresa.</p>
        </div>
        <button data-testid="users-new" onClick={() => setForm({ ...empty })} className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2">
          <Plus className="w-4 h-4" /> Novo usuário
        </button>
      </div>

      {form && (
        <div className="border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="users-form">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Nome</span>
            <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">E-mail</span>
            <input data-testid="user-email" disabled={!!form.id} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">{form.id ? "Nova senha (opcional)" : "Senha"}</span>
            <input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Perfil</span>
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm">
              {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Empresas (segure CTRL para múltiplas)</span>
            <select data-testid="user-companies" multiple value={form.company_ids} onChange={(e) => setForm({ ...form, company_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} className="w-full border border-slate-300 px-2 py-2 text-sm h-24">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia}</option>)}
            </select>
          </label>
          <div className="md:col-span-3 flex justify-end gap-2 pt-2">
            <button onClick={() => setForm(null)} className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 hover:border-slate-900">Cancelar</button>
            <button data-testid="user-save" onClick={save} className="px-4 py-2 text-xs font-bold uppercase bg-[#002EA6] hover:bg-[#00207A] text-white">Salvar</button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
            <tr>
              <th className="text-left px-3 py-2.5">Nome</th>
              <th className="text-left px-3 py-2.5">E-mail</th>
              <th className="text-left px-3 py-2.5">Perfil</th>
              <th className="text-left px-3 py-2.5">Empresas</th>
              <th className="text-right px-3 py-2.5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="5" className="py-10 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>)}
            {list.map((u) => (
              <tr key={u.id} className="t-row border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2.5">{u.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2.5">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-900 text-white">{u.role}</span>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{u.company_ids?.length || 0} empresa(s)</td>
                <td className="px-3 py-2.5 text-right">
                  <button data-testid={`user-edit-${u.email}`} onClick={() => setForm({ ...u, password: "" })} className="p-1.5 hover:bg-slate-200 text-slate-600"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`user-del-${u.email}`} onClick={() => remove(u)} className="p-1.5 hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
